import type { AccountView, ContentResponse, LevelView, ProgressResponse } from '@bioforge/protocol';
import { Container, Graphics, Text } from 'pixi.js';
import { BOSS_NAMES, VESSEL_NAMES } from './labels.js';
import {
  AccountOverlay,
  accountErrorText,
  type AccountFormMode,
} from './overlays/account-overlay.js';
import { reducedMotion } from './overlays/html.js';
import { ConfirmOverlay, RoomOverlay } from './overlays/room-overlay.js';
import { SettingsOverlay } from './overlays/settings-overlay.js';
import type { AppContext, Navigator, Scene } from './scene.js';
import { seenTracker } from './story/story.js';
import { DesignFrame, currentFrame, screenBackground } from './ui/frame.js';
import { idle, microbeIdle, setReducedMotion } from './ui/motion.js';
import { artSprite } from './ui/textures.js';
import { COLOR, textStyle } from './ui/theme.js';
import { NetChip, UiButton, speechBubble, vertical } from './ui/widgets.js';
import { JarView, jarFromRows } from './view/jar-view.js';

/** Банка главного экрана (макет, пресет «menu»): Спорница и микробы трёх видов. */
const MENU_JAR = [
  '.. .. .. .. .. .. .. ..',
  '.. .. .. L2a R1a .. .. ..',
  '.. .. .. .. .. .. .. ..',
  '.. V0 .. .. .. .. V2 ..',
  '.. .. .. .. .. V1 .. ..',
  'V2 .. V1 .. .. .. .. V0',
  '.. .. .. .. .. .. V1 ..',
  '.. V0 .. .. .. .. .. ..',
  'V1 .. .. .. .. .. V2 ..',
  '.. V2 L0 R0 V1 .. .. V0',
  'V0 .. V1 .. V2 V0 .. V2',
  '.. V1 .. V0 .. V2 V1 ..',
];

/** Следующий уровень экспедиции: последний открытый, ещё не пройденный; null — всё пройдено. */
export function nextLevel(content: ContentResponse, progress: ProgressResponse): LevelView | null {
  const levels = content.chapters.flatMap((chapter) => chapter.levels);
  const open = progress.unlocked.at(-1);
  const level = levels.find((l) => l.id === open);
  return level && !progress.levels[level.id] ? level : null;
}

function levelCaption(level: LevelView): string {
  const where = `Глава ${level.chapter} · ${VESSEL_NAMES[level.vessel]} · уровень ${level.index}`;
  return level.bossId ? `${where} — ${BOSS_NAMES[level.bossId]}` : where;
}

/** Главный экран по макету «Главный экран». */
export class MenuScene implements Scene {
  readonly view: DesignFrame;
  private overlay: { destroy(): void } | null = null;
  private readonly stops: (() => void)[] = [];
  /** Телефон в портрете: всё в одну колонку, кнопки крупнее (SPEC.md, 16.3). */
  private readonly tall: boolean;
  private readonly toast: Text;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly nav: Navigator,
    private readonly ctx: AppContext,
    notice = '',
  ) {
    const frame = currentFrame();
    this.tall = frame.width < frame.height;
    this.view = new DesignFrame(
      this.tall
        ? screenBackground(
            0.5,
            0.35,
            [
              { x: 480, y: 460, r: 300, color: 0xd94b8c, alpha: 0.22 },
              { x: 100, y: 1000, r: 300, color: 0x36e2d0, alpha: 0.14 },
            ],
            frame,
          )
        : screenBackground(0.7, 0.5, [
            { x: 1000, y: 480, r: 320, color: 0xd94b8c, alpha: 0.22 },
            { x: 120, y: 780, r: 320, color: 0x36e2d0, alpha: 0.14 },
          ]),
      frame,
    );
    const root = this.view.content;

    this.decorations(root);
    this.header(root);
    this.navigation(root);

    const footer = new Text({
      text: 'Версия 0.1 · прототип        Игра не является медицинской рекомендацией',
      style: textStyle({ size: 13, color: COLOR.textFaint }),
    });
    footer.position.set(this.tall ? 22 : 120, frame.height - 28 - footer.height);
    if (this.tall) footer.text = 'Версия 0.1 · игра не является медицинской рекомендацией';
    this.toast = new Text({
      text: notice,
      style: textStyle({ size: 16, color: 0xffb3c8, weight: '500', wrap: this.tall ? 676 : 440 }),
    });
    this.toast.position.set(this.tall ? 22 : 120, this.tall ? 690 : 750);
    root.addChild(footer, this.toast);
  }

  private header(root: Container): void {
    // Логотип: знак и надпись «BioForge».
    const logo = new Container();
    logo.position.set(120, 110);
    const mark = artSprite('logo', 88);
    mark.position.set(44, 44);
    // Надпись целиком белая, «Bio» поверх — мятная: буквы стоят ровно, как в одной строке.
    const word = new Text({
      text: 'BioForge',
      style: textStyle({ size: 49, display: true, weight: '900', color: COLOR.white, shadow: 6 }),
    });
    const bio = new Text({
      text: 'Bio',
      style: textStyle({ size: 49, display: true, weight: '900', color: COLOR.mint }),
    });
    for (const part of [word, bio]) {
      part.anchor.set(0, 0.5);
      part.position.set(88 + 21, 44);
    }
    logo.addChild(mark, word, bio);
    const tagline = new Text({
      text: 'Медицинская головоломка: собирай капсулы по цвету и вылечи лабораторию от микробов',
      style: textStyle({ size: 19, color: COLOR.textSoft, wrap: 450, lineHeight: 28 }),
    });
    tagline.position.set(120, 212);
    if (this.tall) {
      logo.position.set((720 - logo.width) / 2, 96);
      tagline.style.wordWrapWidth = 600;
      tagline.style.align = 'center';
      tagline.anchor.set(0.5, 0);
      tagline.position.set(360, 200);
    }
    root.addChild(logo, tagline);

    // Справа сверху: сеть, аккаунт, настройки.
    const chip = new NetChip();
    const account = this.accountButton();
    const settings = new UiButton({
      width: 44,
      height: 44,
      label: '',
      radius: 12,
      icon: { key: 'icon-gear', size: 18 },
      center: true,
      onTap: () => this.openSettings(),
    });
    settings.accessibleTitle = 'Настройки';
    settings.position.set(-44, 0);
    account.position.set(settings.x - 10 - account.width, 0);
    // На телефоне группа крупнее — кнопки не меньше зоны касания 44 px.
    const tools = new Container();
    tools.addChild(chip, account, settings);
    if (this.tall) tools.scale.set(1.4);
    tools.position.set(this.view.size.width - (this.tall ? 22 : 40), this.tall ? 18 : 24);
    this.stops.push(
      this.ctx.connection.watch((state) => {
        chip.set(state);
        chip.position.set(account.x - 10 - chip.width, 0);
      }),
    );
    root.addChild(tools);
  }

  private accountButton(): Container {
    const account = this.ctx.account;
    const guest = account.kind === 'guest';
    const name = new Text({
      text: guest ? 'Гость' : account.name,
      style: textStyle({ size: 14, weight: '500' }),
    });
    const sub = new Text({
      text: guest ? 'Привязать аккаунт' : (account.email ?? ''),
      style: textStyle({ size: 12, color: COLOR.textMuted }),
    });
    const width = 6 + 32 + 10 + Math.max(name.width, sub.width) + 14;
    const button = new Container();
    const bg = new Graphics()
      .roundRect(0, 0, width, 44, 12)
      .fill(vertical(COLOR.panelTop, COLOR.panelBottom))
      .stroke({ width: 2, color: COLOR.border });
    bg.roundRect(6, 6, 32, 32, 9).fill(COLOR.teal).stroke({ width: 2, color: COLOR.ink });
    const avatar = artSprite('icon-user', 18);
    avatar.position.set(22, 22);
    name.position.set(48, 6);
    sub.position.set(48, 23);
    button.addChild(bg, avatar, name, sub);
    button.eventMode = 'static';
    button.cursor = 'pointer';
    button.accessible = true;
    button.accessibleType = 'button';
    button.accessibleTitle = guest ? 'Гость: привязать аккаунт' : `${account.name}: аккаунт`;
    button.on('pointertap', () => (guest ? this.openAccount('register') : this.confirmLogout()));
    return button;
  }

  private navigation(root: Container): void {
    const next = nextLevel(this.ctx.content, this.ctx.progress);
    const w = this.tall ? 676 : 440;
    const h = this.tall ? 84 : 70;
    const x = this.tall ? 22 : 120;
    const items: UiButton[] = [
      new UiButton({
        width: w,
        height: this.tall ? 100 : 84,
        variant: 'primary',
        radius: 18,
        label: next ? 'Продолжить' : 'Экспедиция пройдена',
        fontSize: 22,
        sub: next ? levelCaption(next) : 'Переиграйте любой уровень',
        icon: { key: 'icon-play', tile: COLOR.ink },
        onTap: () => (next ? this.nav.play(next) : this.nav.expedition()),
      }),
      new UiButton({
        width: w,
        height: h,
        label: 'Экспедиция',
        sub: 'Главы, уровни и боссы',
        icon: { key: 'icon-map', tile: COLOR.mint },
        onTap: () => this.nav.expedition(),
      }),
      new UiButton({
        width: w,
        height: h,
        label: 'Дуэль',
        sub: '1 на 1 по сети · у каждого свой босс',
        icon: { key: 'icon-duel', tile: COLOR.pink },
        onTap: () => this.openRoom('versus'),
      }),
      new UiButton({
        width: w,
        height: h,
        label: 'Кооператив',
        sub: 'Пилот и Стратег против общего босса',
        icon: { key: 'icon-coop', tile: COLOR.gold },
        onTap: () => this.openRoom('coop'),
      }),
    ];
    let y = this.tall ? 720 : 330;
    for (const item of items) {
      item.position.set(x, y);
      y += item.height + 12;
      root.addChild(item);
    }
    const half = this.tall ? (w - 12) / 2 : 214;
    const encyclopedia = new UiButton({
      width: half,
      height: this.tall ? h : 60,
      label: 'Энциклопедия',
      fontSize: 14,
      center: true,
      icon: { key: 'icon-book', size: 22 },
      onTap: () => this.nav.encyclopedia(),
    });
    const shop = new UiButton({
      width: half,
      height: this.tall ? h : 60,
      variant: 'gold',
      label: 'Магазин',
      fontSize: 14,
      center: true,
      icon: { key: 'icon-cart', size: 22 },
      onTap: () => this.nav.shop(),
    });
    encyclopedia.position.set(x, y);
    shop.position.set(x + half + 12, y);
    root.addChild(encyclopedia, shop);
  }

  private decorations(root: Container): void {
    const floaters: [number, number, number, number][] = this.tall
      ? [
          [0, 96, 360, 96],
          [1, 640, 340, 80],
          [2, 630, 620, 90],
        ]
      : [
          [0, 700, 210, 120],
          [1, 1328, 258, 96],
          [2, 1305, 695, 110],
        ];
    floaters.forEach(([color, x, y, size], i) => {
      const holder = new Container();
      holder.position.set(x, y);
      const microbe = artSprite(`microbe-${color}`, size);
      holder.addChild(microbe);
      root.addChild(holder);
      this.stops.push(idle(holder, 'float', i * 1.3), microbeIdle(microbe, color, i));
    });
    const armored = new Container();
    armored.position.set(this.tall ? 100 : 740, this.tall ? 610 : 700);
    armored.addChild(artSprite('microbe-2', 80 * 0.74), artSprite('armor', 80));
    root.addChild(armored);
    this.stops.push(idle(armored, 'float', 0.8));

    const jar = new JarView(this.tall ? 28 : 40);
    jar.position.set(this.tall ? 232 : 860, this.tall ? 280 : 150);
    jar.render({
      width: 8,
      height: MENU_JAR.length,
      cells: jarFromRows(MENU_JAR),
      boss: { id: 'spornitsa', x: 3, y: 7, width: 2, height: 2 },
    });
    root.addChild(jar);
    this.stops.push(() => jar.destroy());

    const bubble = speechBubble('Спорница', 'Эта банка — моя, доктор!', this.tall ? 190 : 210);
    if (this.tall) bubble.position.set(232 + 10 - bubble.width, 440);
    else bubble.position.set(640, 380);
    root.addChild(bubble);
    this.stops.push(idle(bubble, 'talk'));
  }

  private say(text: string): void {
    this.toast.text = text;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      if (!this.toast.destroyed) this.toast.text = '';
    }, 4000);
  }

  private open(overlay: { destroy(): void }): void {
    this.overlay?.destroy();
    this.overlay = overlay;
  }

  private openRoom(mode: 'versus' | 'coop'): void {
    this.open(
      new RoomOverlay(mode, (intent) => {
        this.overlay = null;
        if (intent) this.nav.online(intent);
      }),
    );
  }

  private openSettings(): void {
    this.open(
      new SettingsOverlay(this.ctx.api, this.ctx.settings, (saved) => {
        this.overlay = null;
        if (!saved) return;
        this.ctx.settings = saved;
        setReducedMotion(reducedMotion(saved.accessibility.reducedMotion));
        // Анимации главного экрана перезапускаются с новой настройкой.
        this.nav.menu();
      }),
    );
  }

  private openAccount(mode: AccountFormMode): void {
    this.open(
      new AccountOverlay(
        this.ctx.api,
        mode,
        (account) => {
          this.overlay = null;
          if (account) void this.switchAccount(account, mode === 'register');
        },
        (other) => this.openAccount(other),
      ),
    );
  }

  private confirmLogout(): void {
    this.open(
      new ConfirmOverlay(
        'Выйти из аккаунта?',
        'Прогресс останется в аккаунте. На этом устройстве начнётся игра новым гостем.',
        'Выйти',
        (yes) => {
          this.overlay = null;
          if (!yes) return;
          this.ctx.api.logout().then(
            (guest) => this.switchAccount(guest, false),
            (error: unknown) => this.say(accountErrorText(error)),
          );
        },
      ),
    );
  }

  /** После входа, выхода и регистрации: новый аккаунт, его прогресс и профиль с сервера. */
  private async switchAccount(account: AccountView, keepsProgress: boolean): Promise<void> {
    this.ctx.account = account;
    try {
      if (!keepsProgress) {
        const [progress, profile, shop] = await Promise.all([
          this.ctx.api.progress(),
          this.ctx.api.profile(),
          this.ctx.api.shop(),
        ]);
        this.ctx.progress = progress;
        this.ctx.shop = shop;
        this.ctx.seen = seenTracker(this.ctx.api, profile.seen);
        this.ctx.settings = profile.settings;
        setReducedMotion(reducedMotion(profile.settings.accessibility.reducedMotion));
      }
    } catch (error) {
      this.say(accountErrorText(error));
      return;
    }
    this.nav.menu();
  }

  destroy(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.stops.splice(0).forEach((stop) => stop());
    this.overlay?.destroy();
    this.view.destroy();
  }
}
